import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DollarSign, TrendingUp, TrendingDown, PieChart, BarChart3,
  Building2, MapPin, FolderKanban, Filter, Download, RefreshCw,
  ArrowUpRight, ArrowDownRight, Wallet, CreditCard, Receipt,
  AlertCircle, Plus, Settings, Database, Target
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const AccountingDashboard = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterProject, setFilterProject] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchDashboardData();
      fetchMasterData();
    }
  }, [selectedCompany, dateFrom, dateTo, filterProject, filterLocation, filterBranch]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/accounting/companies');
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [projectsRes, locationsRes, branchesRes] = await Promise.all([
        api.get(`/accounting/projects?company_id=${selectedCompany}`),
        api.get(`/accounting/locations?company_id=${selectedCompany}`),
        api.get(`/accounting/branches?company_id=${selectedCompany}`)
      ]);
      setProjects(projectsRes.data);
      setLocations(locationsRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (filterProject !== 'all') params.append('project_id', filterProject);
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterBranch !== 'all') params.append('branch_id', filterBranch);
      
      const response = await api.get(`/accounting/dashboard/${selectedCompany}?${params.toString()}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)} K`;
    return num?.toFixed(0) || '0';
  };

  const hasAccess = useSectionAccess('/accounting');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access Accounting.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accounting Dashboard</h1>
            <p className="text-gray-500">Financial overview and MIS reports</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchDashboardData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/accounting/master-data'}>
              <Database className="h-4 w-4 mr-2" />
              Master Data
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/accounting/transactions'}>
              <Receipt className="h-4 w-4 mr-2" />
              Transactions
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/accounting/reports'}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Reports
            </Button>
            <Button onClick={() => window.location.href = '/accounting/advanced-mis'}>
              <Target className="h-4 w-4 mr-2" />
              Advanced MIS
            </Button>
          </div>
        </div>

        {/* Company Selector & Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Company</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger data-testid="select-company">
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Project</label>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Location</label>
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Branch</label>
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        {dashboardData && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 font-medium">Total Revenue</p>
                    <p className="text-xl font-bold text-emerald-900">{formatCurrency(dashboardData.kpis.total_revenue)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-600 font-medium">Total Expenses</p>
                    <p className="text-xl font-bold text-red-900">{formatCurrency(dashboardData.kpis.total_expenses)}</p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${dashboardData.kpis.net_profit >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-medium ${dashboardData.kpis.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Profit</p>
                    <p className={`text-xl font-bold ${dashboardData.kpis.net_profit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                      {formatCurrency(dashboardData.kpis.net_profit)}
                    </p>
                    <p className="text-xs text-gray-500">{dashboardData.kpis.profit_margin}% margin</p>
                  </div>
                  {dashboardData.kpis.net_profit >= 0 ? 
                    <ArrowUpRight className="h-8 w-8 text-blue-500" /> : 
                    <ArrowDownRight className="h-8 w-8 text-orange-500" />
                  }
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-purple-600 font-medium">GST Net</p>
                    <p className="text-xl font-bold text-purple-900">{formatCurrency(dashboardData.kpis.gst_net)}</p>
                    <p className="text-xs text-gray-500">
                      {dashboardData.kpis.gst_net >= 0 ? 'Payable' : 'Receivable'}
                    </p>
                  </div>
                  <Receipt className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Receivables</p>
                    <p className="text-xl font-bold text-amber-900">{formatCurrency(dashboardData.kpis.outstanding_receivables)}</p>
                  </div>
                  <Wallet className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-600 font-medium">Payables</p>
                    <p className="text-xl font-bold text-indigo-900">{formatCurrency(dashboardData.kpis.outstanding_payables)}</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Revenue & Expense Breakdown */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <TrendingUp className="h-5 w-5" />
                  Revenue Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(dashboardData.revenue_breakdown).length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No revenue data for selected period</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(dashboardData.revenue_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10)
                      .map(([name, amount]) => (
                        <div key={name} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 truncate max-w-[200px]">{name}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-500 h-2 rounded-full" 
                                style={{ width: `${(amount / dashboardData.kpis.total_revenue * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-900 w-24 text-right">
                              {formatCurrency(amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expense Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <TrendingDown className="h-5 w-5" />
                  Expense Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(dashboardData.expense_breakdown).length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No expense data for selected period</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(dashboardData.expense_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .map(([group, amount]) => (
                        <div key={group} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 capitalize">
                            {group.replace(/_/g, ' ')}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-red-500 h-2 rounded-full" 
                                style={{ width: `${(amount / dashboardData.kpis.total_expenses * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-900 w-24 text-right">
                              {formatCurrency(amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Stats */}
        {dashboardData && (
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <FolderKanban className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{dashboardData.counts.projects}</p>
                <p className="text-sm text-gray-500">Active Projects</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <MapPin className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{dashboardData.counts.locations}</p>
                <p className="text-sm text-gray-500">Locations</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Receipt className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{dashboardData.counts.transactions}</p>
                <p className="text-sm text-gray-500">Transactions</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {loading && !dashboardData && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* No Company State */}
        {!loading && companies.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Companies Setup</h3>
              <p className="text-gray-500 mb-4">Start by creating a company to use the accounting module</p>
              <Button onClick={() => window.location.href = '/accounting/master-data'}>
                <Plus className="h-4 w-4 mr-2" />
                Setup Master Data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AccountingDashboard;
